import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isRoll, isSticker, isCustomShapeSticker, isSheetFlow, isDesignIn, isStickerFlow, isShortWePrintFlow } from '@app/utils/utils';
import { getAvailableHelpSections, getAvailableHelpTopics } from '@app/utils/help';
import { closeHelp, showHelp, resetHelpSectionTopic } from '@app/actions/helpActions';
import {
  HELP_SECTION_PRODUCT_TYPE,
  HELP_SECTION_ART_SIZE_POSITION,
  HELP_SECTION_UPLOAD_ART,
  HELP_SECTION_WHITE_INK,
  HELP_SECTION_UPLOAD_CODES,
  HELP_SECTION_REQUEST_CODES,
  HELP_SECTION_SAFETY_TRIM_AND_BLEED,
} from '@app/constants';

import ProductTypeSection from './sections/ProductTypeSection';
import UploadArtSection from './sections/UploadArtSection';
import RequestCodesSection from './sections/RequestCodesSection';
import ArtSizePositionSection from './sections/ArtSizePositionSection';
import WhiteInkSection from './sections/WhiteInkSection';
import UploadCodesSection from './sections/UploadCodesSection';
import SafetyTrimAndBleed from './sections/SafetyTrimAndBleed';

import DefaultButton from '@components/buttons/default-btn/DefaultButton';
import LocalizedText from '@LocalizedText';
import useFocusTrap from '@app/hooks/use-focus-trap';
import './HelpContent.scss';

const getHelpSectionComponent = ({
  currentHelpSection,
  currentHelpSectionTopic,
  scrollToTopic,
  isRollLabel,
  isKnownShapeSticker,
  isCustomShapeSticker,
  isSheetFlow,
  isDesignIn,
  isStickerFlow,
  isShortWePrintFlow,
}) => {
  const helpSectionComponents = {
    [HELP_SECTION_PRODUCT_TYPE]: ProductTypeSection,
    [HELP_SECTION_UPLOAD_ART]: UploadArtSection,
    [HELP_SECTION_ART_SIZE_POSITION]: ArtSizePositionSection,
    [HELP_SECTION_REQUEST_CODES]: RequestCodesSection,
    [HELP_SECTION_UPLOAD_CODES]: UploadCodesSection,
    [HELP_SECTION_WHITE_INK]: WhiteInkSection,
    [HELP_SECTION_SAFETY_TRIM_AND_BLEED]: SafetyTrimAndBleed,
  };

  const HelpSectionComponent = helpSectionComponents[currentHelpSection];
  return HelpSectionComponent ? (
    <HelpSectionComponent
      activeTopic={currentHelpSectionTopic}
      scrollToTopic={scrollToTopic}
      isKnownShapeSticker={isKnownShapeSticker}
      isCustomShapeSticker={isCustomShapeSticker}
      isRoll={isRollLabel}
      isSheetFlow={isSheetFlow}
      isStickerFlow={isStickerFlow}
      isDesignIn={isDesignIn}
      isShortWePrintFlow={isShortWePrintFlow}
    />
  ) : null;
};

const HelpContent = ({
  flowConfig,
  matrix,
  flow,
  isRollLabel,
  isKnownShapeSticker,
  isCustomShapeSticker,
  closeHelp,
  showHelp,
  currentHelpSection,
  currentHelpSectionTopic,
  resetHelpSectionTopic,
  isSheetFlow,
  isDesignIn,
  isStickerFlow,
  isShortWePrintFlow,
  activated,
}) => {
  const [helpSections, setHelpSections] = useState([]);
  const [helpTopicsList, setHelpTopicsList] = useState([]);

  const [helpTopicItems, setHelpTopicItems] = useState([]);
  const [helpSectionComponent, setHelpSectionComponent] = useState(<></>);

  const canRenderContent = useMemo(() => !!helpTopicItems.length, [helpTopicItems]);
  const helpSectionContainerRef = useRef();
  const helpTopicListRef = useRef();
  const helpContentContainerRef = useRef();

  const scrollToTopic = useCallback(
    topicKey => {
      const { current: helpSectionContainer } = helpSectionContainerRef;
      const isTitleTopic = helpSections.some(({ titleKey }) => titleKey === topicKey);

      let anchorPosition = 0;

      if (!isTitleTopic) {
        const anchorEl = document.getElementById(topicKey) || {};
        const { offsetTop: scrollTo = 0 } = anchorEl;
        anchorPosition = parseFloat(scrollTo);
      }

      helpSectionContainer.scrollTo({ top: anchorPosition, behavior: 'smooth' });
      resetHelpSectionTopic();
    },
    [helpSections, resetHelpSectionTopic],
  );

  const createHelpTopicsList = useCallback(
    ({ helpSections, helpTopicsList, currentHelpSection }) =>
      helpTopicsList.map(({ topicKey, sectionName, isSubsection = false }) => {
        const { titleKey: currentSectionTitle } = helpSections.find(({ name }) => name === currentHelpSection) || {};
        const className = classnames('help-topic-list-item', { active: topicKey === currentSectionTitle, subsection: isSubsection });
        const setTopic = () => showHelp(sectionName, topicKey);

        return (
          <div
            key={topicKey}
            onClick={setTopic}
            onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setTopic()}
            className={className}
            tabIndex={0}
            role="button"
          >
            <LocalizedText id={`qau-${topicKey}`} />
          </div>
        );
      }),
    [showHelp],
  );

  useEffect(() => {
    const availableHelpSections = getAvailableHelpSections({
      flowConfig,
      matrix,
    });
    const availableHelpTopics = getAvailableHelpTopics(availableHelpSections);

    setHelpSections(availableHelpSections);
    setHelpTopicsList(availableHelpTopics);
  }, [flowConfig, flow, matrix]);

  useEffect(() => {
    const helpTopicItems = createHelpTopicsList({ helpSections, helpTopicsList, currentHelpSection });
    const helpSectionComponent = getHelpSectionComponent({
      currentHelpSection,
      currentHelpSectionTopic,
      scrollToTopic,
      isRollLabel,
      isKnownShapeSticker,
      isCustomShapeSticker,
      isSheetFlow,
      isDesignIn,
      isStickerFlow,
      isShortWePrintFlow,
    });

    setHelpTopicItems(helpTopicItems);
    setHelpSectionComponent(helpSectionComponent);
  }, [
    helpSections,
    helpTopicsList,
    currentHelpSection,
    currentHelpSectionTopic,
    createHelpTopicsList,
    scrollToTopic,
    isRollLabel,
    isKnownShapeSticker,
    isCustomShapeSticker,
    isSheetFlow,
    isDesignIn,
    isStickerFlow,
    isShortWePrintFlow,
  ]);

  useFocusTrap(helpContentContainerRef, activated && canRenderContent, {
    onEscape: closeHelp,
    focusSelector: '.help-topic-list-item.active',
  });

  return (
    canRenderContent && (
      <div id="qau-help-content-container" ref={helpContentContainerRef}>
        <div className="help-content">
          <div className="help-topic-list" ref={helpTopicListRef}>
            {helpTopicItems}
          </div>
          <div className="help-section-container" ref={helpSectionContainerRef}>
            {helpSectionComponent}
          </div>
        </div>
        <div className="help-close-btn">
          <DefaultButton id="qau-close-help-btn" locKey="qau-close-help-btn" onClick={closeHelp} />
        </div>
      </div>
    )
  );
};

const mapStateToProps = ({ help: { currentHelpSection, currentHelpSectionTopic, activated }, matrix }, { flowConfig }) => ({
  matrix,
  currentHelpSection,
  currentHelpSectionTopic,
  activated,
  isRollLabel: isRoll(flowConfig),
  isKnownShapeSticker: isSticker(flowConfig),
  isCustomShapeSticker: isCustomShapeSticker(flowConfig),
  isSheetFlow: isSheetFlow(flowConfig),
  isStickerFlow: isStickerFlow(flowConfig),
  isDesignIn: isDesignIn(flowConfig),
  isShortWePrintFlow: isShortWePrintFlow(flowConfig),
});

const mapDispatchToProps = {
  resetHelpSectionTopic,
  closeHelp,
  showHelp,
};

HelpContent.propTypes = {
  showHelp: PropTypes.func.isRequired,
  currentHelpSection: PropTypes.string,
  currentHelpSectionTopic: PropTypes.string,
  resetHelpSectionTopic: PropTypes.func.isRequired,
  flowConfig: PropTypes.object.isRequired,
  closeHelp: PropTypes.func.isRequired,
  isRollLabel: PropTypes.bool.isRequired,
  isKnownShapeSticker: PropTypes.bool.isRequired,
  isCustomShapeSticker: PropTypes.bool.isRequired,
  isSheetFlow: PropTypes.bool.isRequired,
  isStickerFlow: PropTypes.bool.isRequired,
  isDesignIn: PropTypes.bool.isRequired,
  isShortWePrintFlow: PropTypes.bool.isRequired,
  matrix: PropTypes.object.isRequired,
  flow: PropTypes.object.isRequired,
  activated: PropTypes.bool.isRequired,
};

export default inject('flowConfig', 'flow')(connect(mapStateToProps, mapDispatchToProps)(HelpContent));
